import React from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useResearchAdmin } from '@/hooks/useResearchAdmin';
import { toast } from 'sonner';

const PANELS = [
  {
    id: 'intensity_distribution',
    title: 'Intensity gauge by category',
    description: 'How aggressively users dial fences and listening rules per platform/RSS category.',
    rpc: 'research_intensity_distribution',
    columns: ['category', 'gauge', 'n'],
  },
  {
    id: 'toxicity_threshold_histogram',
    title: 'Toxicity threshold preference',
    description: 'Distribution of user-set toxicity fence thresholds across the cohort.',
    rpc: 'research_toxicity_threshold_histogram',
    columns: ['threshold', 'n'],
  },
  {
    id: 'review_engagement',
    title: 'Reviewed Queue engagement',
    description: 'Approved vs hidden vs still-pending across all fenced/queued items.',
    rpc: 'research_review_engagement',
    columns: ['outcome', 'n'],
  },
  {
    id: 'activity_hour_distribution',
    title: 'Activity by hour (UTC)',
    description: 'Surface visits over the last 30 days, bucketed by hour of day.',
    rpc: 'research_activity_hour_distribution',
    columns: ['hour_utc', 'n'],
  },
  {
    id: 'rule_abandonment',
    title: 'Rule abandonment',
    description: 'Active rules vs rules currently parked at gauge 0.',
    rpc: 'research_rule_abandonment',
    columns: ['status', 'n'],
  },
  {
    id: 'top_fenced_tags',
    title: 'Top fenced rule tags',
    description: 'Most-applied rule names on hidden items.',
    rpc: 'research_top_fenced_tags',
    columns: ['tag', 'n'],
  },
] as const;

type PanelDef = (typeof PANELS)[number];

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL ?? 'https://ifkrklrmsjszdloohxvx.supabase.co';

function BarRow({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-32 truncate text-muted-foreground">{label}</div>
      <div className="flex-1 h-3 bg-muted rounded-sm overflow-hidden">
        <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 tabular-nums text-right">{value}</div>
    </div>
  );
}

function PanelCard({ panel, cohortSize }: { panel: PanelDef; cohortSize: number }) {
  const insufficient = cohortSize < 10;

  const query = useQuery({
    queryKey: ['research-panel', panel.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(panel.rpc as any);
      if (error) throw error;
      return (data ?? []) as Record<string, any>[];
    },
    staleTime: 60_000,
    enabled: !insufficient,
  });

  const downloadCsv = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        toast.error('Sign in expired — please refresh.');
        return;
      }
      const res = await fetch(`${SUPABASE_URL}/functions/v1/research-export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ panel: panel.id }),
      });
      if (!res.ok) {
        toast.error(`Export failed: ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${panel.id}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    }
  };

  const rows = query.data ?? [];
  const labelKey = panel.columns[0];
  const valueKey = panel.columns[panel.columns.length - 1] as 'n';
  const groupKey = panel.columns.length > 2 ? (panel.columns[1] as string) : null;
  const max = rows.reduce((m, r) => Math.max(m, Number(r[valueKey] ?? 0)), 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base">{panel.title}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">{panel.description}</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadCsv} disabled={insufficient}>
          <Download className="h-3 w-3 mr-1" />
          CSV
        </Button>
      </CardHeader>
      <CardContent>
        {insufficient ? (
          <p className="text-xs text-muted-foreground py-6 text-center">
            Cohort below the 10-user privacy floor. No data shown.
          </p>
        ) : query.isLoading ? (
          <p className="text-xs text-muted-foreground py-6 text-center">Loading…</p>
        ) : query.isError ? (
          <p className="text-xs text-destructive py-6 text-center">
            {(query.error as Error).message}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground py-6 text-center">No data yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r, idx) => (
              <BarRow
                key={idx}
                label={
                  groupKey
                    ? `${r[labelKey] ?? '—'} · ${groupKey}=${r[groupKey] ?? '—'}`
                    : String(r[labelKey] ?? '—')
                }
                value={Number(r[valueKey] ?? 0)}
                max={max}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const ResearchDashboard: React.FC = () => {
  const { user } = useAuth();
  const adminQuery = useResearchAdmin();

  const cohortQuery = useQuery({
    queryKey: ['research-cohort'],
    enabled: adminQuery.data === true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('research_cohort_size');
      if (error) throw error;
      return Number(data ?? 0);
    },
    staleTime: 60_000,
  });

  if (!user) return <Navigate to="/auth" replace />;
  if (adminQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Checking access…</div>;
  }
  if (adminQuery.data !== true) {
    return <Navigate to="/dashboard" replace />;
  }

  const cohort = cohortQuery.data ?? 0;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Research dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Aggregated, opt-in-only data. All panels enforce a 10-user cohort floor in SQL.
            </p>
          </div>
          <div className="rounded-md border border-border/60 px-3 py-2 text-sm">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Cohort</div>
            <div className="text-2xl font-semibold tabular-nums">
              {cohortQuery.isLoading ? '…' : cohort}
            </div>
            <div className="text-[11px] text-muted-foreground">
              opted-in users
              {cohort < 10 && cohort > 0 && (
                <span className="text-destructive"> · below 10 floor</span>
              )}
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PANELS.map((p) => (
            <PanelCard key={p.id} panel={p} cohortSize={cohort} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResearchDashboard;
